import {logUtishaDebug, logUtishaError, logUtishaTrace} from 'src/app/util/log';
import {CanvasRenderer} from '../CanvasRenderer';

/**
 * Renderer for drawing an HTMLVideoElement on a canvas
 */
export class HTMLVideoElementCanvasRenderer extends CanvasRenderer {
  /**
   * Creates a new HTMLVideoElementCanvasRenderer
   * @param videoElement Video element to render
   * @param _priority Drawing priority (higher means drawn later/on top)
   */
  constructor(
    protected videoElement: HTMLVideoElement,
    priority = 10
  ) {
    super(priority);
    // Ensure video metadata is loaded
    this.prepareVideoElement();
  }

  /**
   * Prepares the video element by ensuring metadata is loaded
   */
  private async prepareVideoElement(): Promise<void> {
    logUtishaDebug(`HTMLVideoElementCanvasRenderer[${this.id}]: preparing video element`);

    // Wait for video metadata to load if not already loaded
    if (!this.videoElement.videoWidth || !this.videoElement.videoHeight) {
      try {
        await new Promise<void>((resolve, reject) => {
          const loadHandler = () => {
            this.videoElement.removeEventListener('loadedmetadata', loadHandler);
            resolve();
          };

          const errorHandler = (error: any) => {
            this.videoElement.removeEventListener('error', errorHandler);
            reject(error);
          };

          this.videoElement.addEventListener('loadedmetadata', loadHandler);
          this.videoElement.addEventListener('error', errorHandler);

          // Add a timeout just in case
          setTimeout(() => {
            this.videoElement.removeEventListener('loadedmetadata', loadHandler);
            this.videoElement.removeEventListener('error', errorHandler);
            resolve();
          }, 1000);
        });
      } catch (error) {
        logUtishaError(`HTMLVideoElementCanvasRenderer[${this.id}]: error loading video metadata: ${error}`);
      }
    }

    logUtishaDebug(`HTMLVideoElementCanvasRenderer[${this.id}]: video ready, dimensions: ${this.videoElement.videoWidth}x${this.videoElement.videoHeight}`);
  }

  /**
   * Initializes the renderer with a canvas
   */
  initialize(canvas: HTMLCanvasElement): void {
    super.initialize(canvas);

    // If video has dimensions and canvas doesn't, set canvas dimensions from video
    if (this.videoElement && this.videoElement.videoWidth && this.videoElement.videoHeight) {
      if (canvas.width === 0 || canvas.height === 0) {
        canvas.width = this.videoElement.videoWidth || 1280;
        canvas.height = this.videoElement.videoHeight || 720;
        logUtishaDebug(`HTMLVideoElementCanvasRenderer[${this.id}]: set canvas dimensions to ${canvas.width}x${canvas.height} from video`);
      }
    }

    logUtishaDebug(`HTMLVideoElementCanvasRenderer[${this.id}]: initialized with canvas ${canvas.width}x${canvas.height}`);
  }

  /**
   * Source video the pipeline can drive via requestVideoFrameCallback.
   * Exposing this lets CanvasRenderingPipeline pick the rVFC fast path.
   */
  getSourceVideoElement(): HTMLVideoElement | null {
    return this.videoElement || null;
  }

  /**
   * Renders the video element to the canvas
   */
  render(): void {
    logUtishaTrace(`HTMLVideoElementCanvasRenderer[${this.id}]: rendering`);
    if (!this.canvas || !this.videoElement) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    try {
      ctx.drawImage(this.videoElement, 0, 0, this.canvas.width, this.canvas.height);
    } catch (error) {
      logUtishaDebug(`HTMLVideoElementCanvasRenderer[${this.id}]: error rendering - ${error}`);
    }
  }

  /**
   * Gets the name of this renderer
   */
  get name(): string {
    return HTMLVideoElementCanvasRenderer.NAME;
  }

  static get NAME() {
    return 'HTMLVideoElementRenderer';
  }

}
